import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, Search, Filter, Eye, Edit, Trash2, X, MapPin, Phone, Mail,
  Clock, AlertTriangle, CheckCircle, Package, User, Calendar, FileText,
  MessageSquare, ChevronDown, MoreHorizontal, Loader2, RefreshCw, Download,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'vivaimpact_service';
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_CONFIRM_TEMPLATE || 'vivaimpact_confirmacao';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '';

const statusConfig = {
  pending: { label: 'Pendente', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
  in_review: { label: 'Em Análise', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Eye },
  approved: { label: 'Aprovado', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  in_progress: { label: 'Em Andamento', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Package },
  completed: { label: 'Concluído', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  rejected: { label: 'Rejeitado', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: X },
  cancelled: { label: 'Cancelado', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', icon: X }
};

const urgenciaConfig = {
  baixa: { label: 'Baixa', color: 'text-emerald-400' },
  normal: { label: 'Normal', color: 'text-blue-400' },
  alta: { label: 'Alta', color: 'text-amber-400' },
  urgente: { label: 'Urgente', color: 'text-red-400' }
};

const tiposDoacaoLabels = {
  alimentos: 'Alimentos',
  roupas: 'Roupas',
  brinquedos: 'Brinquedos',
  materiais_escolares: 'Mat. Escolares',
  higiene: 'Higiene',
  cestas_basicas: 'Cestas Básicas',
  medicamentos: 'Medicamentos',
  moveis: 'Móveis/Eletro',
  outros: 'Outros'
};

const AdminDonations = ({ token }) => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [filters, setFilters] = useState({ status: '', urgencia: '', search: '' });
  const [showFilters, setShowFilters] = useState(false);

  const getAuthHeader = () => ({ 'Authorization': `Bearer ${token}` });

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.urgencia) params.append('urgencia', filters.urgencia);
      if (filters.search) params.append('search', filters.search);

      const response = await fetch(`${BACKEND_URL}/api/donations/requests?${params}`, {
        headers: getAuthHeader()
      });
      const data = await response.json();
      if (data.success) {
        setRequests(data.requests);
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching donations:', error);
      toast.error('Erro ao carregar solicitações');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [filters.status, filters.urgencia]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRequests();
  };

  const updateStatus = async (protocolo, newStatus, note = '') => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/donations/requests/${protocolo}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ status: newStatus, note, send_email: true })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Status atualizado');
        fetchRequests();
        if (selectedRequest?.protocolo === protocolo) {
          setSelectedRequest(prev => ({ ...prev, status: newStatus }));
        }
        
        // Send email notification via EmailJS
        if (data.email_data && EMAILJS_PUBLIC_KEY) {
          try {
            await emailjs.send(
              EMAILJS_SERVICE_ID,
              EMAILJS_TEMPLATE_ID,
              {
                to_email: data.email_data.to_email,
                to_name: data.email_data.to_name,
                protocolo: data.email_data.protocolo,
                old_status: data.email_data.old_status,
                new_status: data.email_data.new_status,
                note: data.email_data.note || 'Sem observações adicionais.',
                from_name: 'Viva Impact'
              },
              EMAILJS_PUBLIC_KEY
            );
            toast.success('Email de notificação enviado!');
          } catch (emailErr) {
            console.error('Email error:', emailErr);
            // Don't show error toast - status was updated successfully
          }
        }
      }
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.urgencia) params.append('urgencia', filters.urgencia);
      
      const response = await fetch(`${BACKEND_URL}/api/donations/requests/export/csv?${params}`, {
        headers: getAuthHeader()
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `solicitacoes_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('CSV exportado!');
      }
    } catch (error) {
      toast.error('Erro ao exportar');
    }
  };

  const getWhatsAppLink = (request) => {
    const phone = request.telefone?.replace(/\D/g, '');
    const message = encodeURIComponent(`Olá ${request.nome}, somos da Viva Impact. Sua solicitação ${request.protocolo} está sendo analisada. Como podemos ajudar?`);
    return `https://wa.me/55${phone}?text=${message}`;
  };

  const deleteRequest = async (protocolo) => {
    if (!window.confirm('Tem certeza que deseja excluir esta solicitação?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/donations/requests/${protocolo}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });

      if (response.ok) {
        toast.success('Solicitação excluída');
        fetchRequests();
        setShowDetail(false);
      }
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: 'Total', value: stats.total || 0, color: 'text-white' },
          { label: 'Pendentes', value: stats.pending || 0, color: 'text-amber-400' },
          { label: 'Em Análise', value: stats.in_review || 0, color: 'text-blue-400' },
          { label: 'Aprovados', value: stats.approved || 0, color: 'text-emerald-400' },
          { label: 'Em Andamento', value: stats.in_progress || 0, color: 'text-purple-400' },
          { label: 'Concluídos', value: stats.completed || 0, color: 'text-green-400' },
          { label: 'Urgentes', value: stats.urgent || 0, color: 'text-red-400' }
        ].map((stat, i) => (
          <div key={i} className="bg-white/[0.02] border border-white/10 rounded-xl p-3">
            <p className="text-slate-500 text-xs">{stat.label}</p>
            <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                placeholder="Buscar por nome, protocolo, email ou cidade..."
                className="w-full pl-10 pr-4 py-2 bg-white/[0.02] border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-[#22c55e]/50 focus:outline-none"
              />
            </div>
          </form>

          <div className="flex gap-2">
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="px-3 py-2 bg-white/[0.02] border border-white/10 rounded-lg text-white focus:outline-none"
            >
              <option value="" className="bg-[#0a0f1e]">Todos Status</option>
              {Object.entries(statusConfig).map(([key, val]) => (
                <option key={key} value={key} className="bg-[#0a0f1e]">{val.label}</option>
              ))}
            </select>

            <select
              value={filters.urgencia}
              onChange={(e) => setFilters(prev => ({ ...prev, urgencia: e.target.value }))}
              className="px-3 py-2 bg-white/[0.02] border border-white/10 rounded-lg text-white focus:outline-none"
            >
              <option value="" className="bg-[#0a0f1e]">Todas Urgências</option>
              {Object.entries(urgenciaConfig).map(([key, val]) => (
                <option key={key} value={key} className="bg-[#0a0f1e]">{val.label}</option>
              ))}
            </select>

            <button
              onClick={fetchRequests}
              className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-slate-400 hover:text-white hover:bg-white/10"
              title="Atualizar"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            
            <button
              onClick={exportCSV}
              className="px-3 py-2 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-lg text-[#22c55e] hover:bg-[#22c55e]/20 flex items-center gap-2"
              title="Exportar CSV"
            >
              <Download className="w-5 h-5" />
              <span className="hidden md:inline">Exportar CSV</span>
            </button>
          </div>
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white/[0.02] border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#22c55e] animate-spin" />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12">
            <Heart className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500">Nenhuma solicitação encontrada</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-slate-400 text-xs font-medium uppercase">Protocolo</th>
                  <th className="text-left p-4 text-slate-400 text-xs font-medium uppercase">Solicitante</th>
                  <th className="text-left p-4 text-slate-400 text-xs font-medium uppercase">Local</th>
                  <th className="text-left p-4 text-slate-400 text-xs font-medium uppercase">Tipo</th>
                  <th className="text-left p-4 text-slate-400 text-xs font-medium uppercase">Urgência</th>
                  <th className="text-left p-4 text-slate-400 text-xs font-medium uppercase">Status</th>
                  <th className="text-left p-4 text-slate-400 text-xs font-medium uppercase">Data</th>
                  <th className="text-center p-4 text-slate-400 text-xs font-medium uppercase">WhatsApp</th>
                  <th className="text-right p-4 text-slate-400 text-xs font-medium uppercase">Ações</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => {
                  const StatusIcon = statusConfig[req.status]?.icon || Clock;
                  return (
                    <tr key={req.protocolo} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="p-4">
                        <span className="font-mono text-[#22c55e] text-sm">{req.protocolo}</span>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-white text-sm">{req.nome}</p>
                          <p className="text-slate-500 text-xs">{req.email}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-slate-300 text-sm">{req.endereco?.cidade} - {req.endereco?.estado}</p>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {req.tipoDoacao?.slice(0, 2).map(t => (
                            <span key={t} className="px-2 py-0.5 bg-white/5 text-slate-400 text-xs rounded">
                              {tiposDoacaoLabels[t] || t}
                            </span>
                          ))}
                          {req.tipoDoacao?.length > 2 && (
                            <span className="px-2 py-0.5 bg-white/5 text-slate-500 text-xs rounded">
                              +{req.tipoDoacao.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`text-sm font-medium ${urgenciaConfig[req.urgencia]?.color || 'text-slate-400'}`}>
                          {urgenciaConfig[req.urgencia]?.label || req.urgencia}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${statusConfig[req.status]?.color || 'bg-slate-500/20 text-slate-400'}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig[req.status]?.label || req.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <p className="text-slate-400 text-xs">{formatDate(req.created_at)}</p>
                      </td>
                      <td className="p-4 text-center">
                        <a
                          href={getWhatsAppLink(req)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center w-8 h-8 bg-[#25D366]/20 text-[#25D366] rounded-lg hover:bg-[#25D366]/30 transition-all"
                          title="Enviar WhatsApp"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </a>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => { setSelectedRequest(req); setShowDetail(true); }}
                            className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg"
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <select
                            value={req.status}
                            onChange={(e) => updateStatus(req.protocolo, e.target.value)}
                            className="px-2 py-1 bg-transparent border border-white/10 rounded text-xs text-slate-300 focus:outline-none cursor-pointer"
                          >
                            {Object.entries(statusConfig).map(([key, val]) => (
                              <option key={key} value={key} className="bg-[#0a0f1e]">{val.label}</option>
                            ))}
                          </select>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetail && selectedRequest && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetail(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0a0f1e] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-[#0a0f1e] border-b border-white/10 p-6 flex items-center justify-between">
                <div>
                  <p className="font-mono text-[#22c55e]">{selectedRequest.protocolo}</p>
                  <h2 className="text-xl font-bold text-white">{selectedRequest.nome}</h2>
                </div>
                <button
                  onClick={() => setShowDetail(false)}
                  className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Status e Urgência */}
                <div className="flex items-center gap-4">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${statusConfig[selectedRequest.status]?.color}`}>
                    {statusConfig[selectedRequest.status]?.label}
                  </span>
                  <span className={`text-sm font-medium ${urgenciaConfig[selectedRequest.urgencia]?.color}`}>
                    Urgência: {urgenciaConfig[selectedRequest.urgencia]?.label}
                  </span>
                </div>

                {/* Contato */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-white/[0.02] rounded-xl">
                    <Mail className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="text-slate-500 text-xs">E-mail</p>
                      <p className="text-white text-sm">{selectedRequest.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white/[0.02] rounded-xl">
                    <Phone className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="text-slate-500 text-xs">Telefone</p>
                      <p className="text-white text-sm">{selectedRequest.telefone}</p>
                    </div>
                  </div>
                </div>

                {/* Endereço */}
                <div className="p-4 bg-white/[0.02] rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-[#22c55e]" />
                    <p className="text-slate-400 text-sm font-medium">Endereço</p>
                  </div>
                  <p className="text-white">
                    {selectedRequest.endereco?.logradouro}, {selectedRequest.endereco?.numero}
                    {selectedRequest.endereco?.complemento && ` - ${selectedRequest.endereco.complemento}`}
                  </p>
                  <p className="text-slate-400 text-sm">
                    {selectedRequest.endereco?.bairro && `${selectedRequest.endereco.bairro}, `}
                    {selectedRequest.endereco?.cidade} - {selectedRequest.endereco?.estado}
                  </p>
                  <p className="text-slate-500 text-xs mt-1">CEP: {selectedRequest.endereco?.cep}</p>
                </div>

                {/* Tipos de Doação */}
                <div>
                  <p className="text-slate-400 text-sm font-medium mb-2">Tipos de Doação Solicitados</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedRequest.tipoDoacao?.map(t => (
                      <span key={t} className="px-3 py-1.5 bg-[#22c55e]/10 text-[#22c55e] text-sm rounded-lg border border-[#22c55e]/30">
                        {tiposDoacaoLabels[t] || t}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Quantidade */}
                <div className="flex items-center gap-3 p-3 bg-white/[0.02] rounded-xl">
                  <User className="w-5 h-5 text-slate-500" />
                  <div>
                    <p className="text-slate-500 text-xs">Pessoas Beneficiadas</p>
                    <p className="text-white">{selectedRequest.quantidade}</p>
                  </div>
                </div>

                {/* Descrição */}
                <div>
                  <p className="text-slate-400 text-sm font-medium mb-2">Descrição da Necessidade</p>
                  <p className="text-slate-300 text-sm p-4 bg-white/[0.02] rounded-xl whitespace-pre-wrap">
                    {selectedRequest.descricao}
                  </p>
                </div>

                {/* Foto */}
                {selectedRequest.fotoUrl && (
                  <div>
                    <p className="text-slate-400 text-sm font-medium mb-2">Foto Enviada</p>
                    <img
                      src={selectedRequest.fotoUrl}
                      alt="Foto da situação"
                      className="w-full h-48 object-cover rounded-xl"
                    />
                  </div>
                )}

                {/* Autorização de Imagem */}
                <div className={`flex items-center gap-3 p-4 rounded-xl ${
                  selectedRequest.autorizacaoImagem === true 
                    ? 'bg-emerald-500/10 border border-emerald-500/30' 
                    : selectedRequest.autorizacaoImagem === false
                    ? 'bg-red-500/10 border border-red-500/30'
                    : 'bg-slate-500/10 border border-slate-500/30'
                }`}>
                  {selectedRequest.autorizacaoImagem === true ? (
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  ) : selectedRequest.autorizacaoImagem === false ? (
                    <X className="w-5 h-5 text-red-400" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-slate-400" />
                  )}
                  <div>
                    <p className="text-slate-500 text-xs">Autorização de Imagem e Voz</p>
                    <p className={`font-medium ${
                      selectedRequest.autorizacaoImagem === true ? 'text-emerald-400' :
                      selectedRequest.autorizacaoImagem === false ? 'text-red-400' : 'text-slate-400'
                    }`}>
                      {selectedRequest.autorizacaoImagem === true ? 'Autorizado' :
                       selectedRequest.autorizacaoImagem === false ? 'Não Autorizado' : 'Não informado'}
                    </p>
                  </div>
                </div>

                {/* Histórico */}
                {selectedRequest.history && selectedRequest.history.length > 0 && (
                  <div>
                    <p className="text-slate-400 text-sm font-medium mb-2">Histórico</p>
                    <div className="space-y-2">
                      {selectedRequest.history.map((h, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-white/[0.02] rounded-lg">
                          <div className={`w-2 h-2 mt-1.5 rounded-full ${statusConfig[h.status]?.color?.split(' ')[0] || 'bg-slate-500'}`} />
                          <div className="flex-1">
                            <p className="text-white text-sm">{h.note}</p>
                            <p className="text-slate-500 text-xs">
                              {formatDate(h.date)} {h.by && `• ${h.by}`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Ações */}
                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <button
                    onClick={() => deleteRequest(selectedRequest.protocolo)}
                    className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg border border-red-500/30 hover:bg-red-500/20 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Excluir
                  </button>
                  
                  <div className="flex gap-2">
                    <select
                      value={selectedRequest.status}
                      onChange={(e) => updateStatus(selectedRequest.protocolo, e.target.value)}
                      className="px-4 py-2 bg-white/[0.02] border border-white/10 rounded-lg text-white focus:outline-none"
                    >
                      {Object.entries(statusConfig).map(([key, val]) => (
                        <option key={key} value={key} className="bg-[#0a0f1e]">{val.label}</option>
                      ))}
                    </select>
                    <a
                      href={`https://wa.me/55${selectedRequest.telefone?.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-[#22c55e] text-[#0a0f1e] font-semibold rounded-lg hover:shadow-[0_0_20px_rgba(34,197,94,0.3)]"
                    >
                      WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDonations;
